import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "./AppShell";
import { Sparks } from "iconoir-react";

export default function ContentStrategist() {
  const [topic, setTopic] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/content/strategy", { focus_topic: topic });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Content strategist" subtitle="// AI-first content plan">
      <div data-testid="content-strategist-page">
        <form onSubmit={run} className="flex flex-col sm:flex-row gap-2 max-w-2xl">
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Optional focus topic (e.g. 'tax for freelancers')"
            data-testid="content-topic-input"
            className="flex-1 bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="content-generate-button"
            className="px-6 py-3 bg-obsidian text-bone font-app font-bold uppercase tracking-wider text-sm hover:bg-intl transition-colors disabled:opacity-60 inline-flex items-center gap-2"
          >
            <Sparks width={16} height={16} /> {busy ? "Generating…" : "Generate plan"}
          </button>
        </form>

        {!data && !busy && (
          <p className="mt-10 text-muted-foreground max-w-2xl">
            Generate a complete AI-first content plan: topical clusters, FAQ ideas, blog ideas and AI snippet opportunities tailored to your business.
          </p>
        )}

        {data && (
          <div className="mt-10 grid grid-cols-12 gap-4 lg:gap-6" data-testid="content-output">
            <div className="col-span-12 lg:col-span-6 border border-border bg-card p-6">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ topical clusters</div>
              <h3 className="font-app text-xl">Build authority around</h3>
              <div className="mt-5 space-y-4">
                {(data.topical_clusters || []).map((c) => (
                  <div key={c.cluster}>
                    <div className="font-app font-medium">{c.cluster}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(c.topics || []).map((t) => (
                        <span key={t} className="px-2 py-1 bg-background border border-border font-mono text-xs">{t}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-span-12 lg:col-span-6 border border-border bg-card p-6">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ FAQ ideas</div>
              <h3 className="font-app text-xl">Add these to your site</h3>
              <ul className="mt-5 space-y-2 text-sm">
                {(data.faq_ideas || []).map((q) => (
                  <li key={q} className="flex gap-3"><span className="text-intl">→</span>{q}</li>
                ))}
              </ul>
            </div>

            <div className="col-span-12 lg:col-span-6 border border-border bg-card p-6">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ blog ideas</div>
              <h3 className="font-app text-xl">Write next</h3>
              <ul className="mt-5 space-y-4 text-sm">
                {(data.blog_ideas || []).map((b) => (
                  <li key={b.title}>
                    <div className="font-app">{b.title}</div>
                    <div className="text-muted-foreground mt-1">{b.angle}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-span-12 lg:col-span-6 border border-border bg-card p-6">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ AI snippet opportunities</div>
              <h3 className="font-app text-xl">Queries AI might ask</h3>
              <ul className="mt-5 space-y-4 text-sm">
                {(data.ai_snippet_opportunities || []).map((s) => (
                  <li key={s.query}>
                    <div className="font-app">"{s.query}"</div>
                    <div className="text-muted-foreground mt-1">{s.answer_outline}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
